import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useIntl } from 'react-intl';

// Direct module import: the shared UI barrel drags the api client into
// every renderer that needs one primitive.
import { Icon } from '@/shared/ui/Icon/Icon';

import { i18n } from '../config';
import { closeKbImage, useKbLightbox } from '../model';

import styles from './KnowledgeBase.module.scss';

/**
 * The article image at full size, over a dark backdrop.
 *
 * It sits above the panel and closes on the backdrop, the close button or
 * Escape. The image is never stretched: it keeps its ratio inside the
 * viewport and the reader scrolls nothing.
 */
export const KbLightbox = () => {
  const intl = useIntl();
  const image = useKbLightbox();
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!image) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeKbImage();
    };

    document.addEventListener('keydown', onKeyDown);
    closeRef.current?.focus();

    return () => {
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [image]);

  if (!image) return null;

  // The lightbox is a React child of the panel overlay, so a click here also
  // reaches the overlay's close handler unless it stops at this element.
  return createPortal(
    <div
      className={styles.lightbox}
      role="dialog"
      aria-modal="true"
      aria-label={image.alt || intl.formatMessage(i18n.imageViewer)}
      data-component="knowledge-base-lightbox"
      onClick={(event) => {
        event.stopPropagation();
        closeKbImage();
      }}
    >
      <button
        ref={closeRef}
        type="button"
        className={styles.lightboxClose}
        aria-label={intl.formatMessage(i18n.close)}
        onClick={(event) => {
          event.stopPropagation();
          closeKbImage();
        }}
      >
        <Icon name="close" size={20} />
      </button>
      <img
        className={styles.lightboxImage}
        src={image.src}
        alt={image.alt ?? ''}
        onClick={(event) => event.stopPropagation()}
      />
    </div>,
    document.body,
  );
};
